<template>
  <section v-if="items.length" class="review-queue card">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0">
          <CygnusIcon name="bolt" class="me-1" />Review queue
          <span class="review-badge">{{ items.length }}</span>
        </h6>
        <button type="button" class="btn btn-link btn-sm p-0" :disabled="loading" @click="load">
          <CygnusIcon name="refresh" class="me-1" />Refresh
        </button>
      </div>
      <p class="text-muted small mb-2">
        A business signal matched a trigger set to review first. Approve to enrol the
        prospect in the flow, or dismiss to skip this one.
      </p>

      <div v-if="error" class="alert alert-danger py-1 px-2 small mb-2">{{ error }}</div>

      <ul class="review-list list-unstyled mb-0">
        <li v-for="it in items" :key="it.id" class="review-item">
          <div class="review-item__who">
            <span class="review-item__company">{{ it.company || it.email || ("Prospect #" + it.prospect_id) }}</span>
            <span v-if="it.contact_name" class="review-item__contact text-muted">{{ it.contact_name }}</span>
            <span v-if="it.email" class="review-item__email text-muted">{{ it.email }}</span>
          </div>
          <div class="review-item__signal">
            <span class="review-evt">
              <CygnusIcon :name="it.event_icon || 'bolt'" class="me-1" />{{ it.event_label }}
            </span>
            <span class="review-item__flow text-muted">→ {{ it.flow_name }}</span>
            <span v-if="it.flow_status && it.flow_status !== 'active'" class="review-pill review-pill--paused">
              flow {{ it.flow_status }}
            </span>
          </div>
          <div v-if="it.event_summary" class="review-item__summary text-muted">{{ it.event_summary }}</div>
          <div class="review-item__actions">
            <button type="button" class="btn btn-sm btn-outline-success" :disabled="busyId === it.id" @click="approve(it)">
              <CygnusIcon name="check" class="me-1" />Approve
            </button>
            <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="busyId === it.id" @click="dismiss(it)">
              Dismiss
            </button>
            <span v-if="it._msg" class="small ms-1" :class="it._ok ? 'text-success' : 'text-danger'">{{ it._msg }}</span>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: "TriggerReviewQueue",
  emits: ["changed"],
  data() {
    return {
      api: useApi(),
      items: [],
      loading: false,
      error: null,
      busyId: null,
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await this.api.get("/api/v1/admin/trigger-enrolments?status=pending");
        this.items = ((res?.data ?? res)?.data || []).map((r) => ({ ...r, _msg: "", _ok: false }));
      } catch (e) {
        // A principal without Outreach access 403s — degrade quietly (no queue).
        this.items = [];
        if (e?.response?.status && e.response.status !== 403) this.error = "Could not load the review queue.";
      } finally {
        this.loading = false;
      }
    },
    async approve(it) {
      this.busyId = it.id;
      try {
        const res = await this.api.post(`/api/v1/admin/trigger-enrolments/${it.id}/approve`, {});
        const status = (res?.data ?? res)?.status;
        this.items = this.items.filter((x) => x.id !== it.id);
        this.$emit("changed");
        void status;
      } catch (e) {
        it._ok = false;
        it._msg = e?.response?.data?.error || "Could not approve.";
      } finally {
        this.busyId = null;
      }
    },
    async dismiss(it) {
      this.busyId = it.id;
      try {
        await this.api.post(`/api/v1/admin/trigger-enrolments/${it.id}/dismiss`, {});
        this.items = this.items.filter((x) => x.id !== it.id);
        this.$emit("changed");
      } catch (e) {
        it._ok = false;
        it._msg = e?.response?.data?.error || "Could not dismiss.";
      } finally {
        this.busyId = null;
      }
    },
  },
};
</script>

<style scoped>
.review-queue { border-left: 3px solid var(--bs-primary, #0d6efd); }
.review-badge {
  display: inline-block;
  min-width: 1.4rem;
  text-align: center;
  background: var(--bs-primary, #0d6efd);
  color: #fff;
  border-radius: 0.75rem;
  font-size: 0.75rem;
  padding: 0.05rem 0.4rem;
  margin-left: 0.25rem;
}
.review-item {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem 0.8rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--bs-border-color, #eee);
}
.review-item:last-child { border-bottom: 0; }
.review-item__who { display: flex; flex-direction: column; min-width: 200px; }
.review-item__company { font-weight: 600; }
.review-item__contact, .review-item__email { font-size: 0.78rem; }
.review-item__signal { display: flex; align-items: center; gap: 0.4rem; flex: 1 1 auto; }
.review-evt {
  font-size: 0.8rem;
  background: var(--bs-tertiary-bg, #f2f2f2);
  border-radius: 0.5rem;
  padding: 0.1rem 0.5rem;
}
.review-item__summary { flex: 1 1 100%; font-size: 0.78rem; font-style: italic; }
.review-pill--paused {
  font-size: 0.72rem;
  background: #fff0d6;
  color: #8a5300;
  border-radius: 0.5rem;
  padding: 0.05rem 0.4rem;
}
.review-item__actions { display: flex; align-items: center; gap: 0.4rem; }
</style>
